// ========= Copyright 2025-2026 @ Eigent.ai All Rights Reserved. =========
// Licensed under the Apache License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License.
// You may obtain a copy of the License at
//
//     http://www.apache.org/licenses/LICENSE-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
// See the License for the specific language governing permissions and
// limitations under the License.
// ========= Copyright 2025-2026 @ Eigent.ai All Rights Reserved. =========

import { ResponsivePanel } from '@/components/remote/ResponsivePanel';
import { Button } from '@/components/ui/button';
import { MorphIcon } from '@/components/ui/morph-icon';
import { useIsDesktop } from '@/hooks/useMediaQuery';
import { cn } from '@/lib/utils';
import {
  type RemoteControlProject,
  type RemoteControlSession,
  type RemoteControlSpace,
  type RemoteControlTarget,
} from '@web/api/remoteControl';
import {
  Check,
  CheckCheck,
  FolderPlus,
  MessageCircle,
  PanelLeftClose,
  Plus,
  RefreshCw,
  Trash2,
} from 'lucide';
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';

interface RemoteSidePanelProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  session: RemoteControlSession | null;
  target: RemoteControlTarget | null;
  space: RemoteControlSpace | null;
  projects: RemoteControlProject[];
  projectLoading: boolean;
  controlLoading: string | null;
  onSelectProject: (project: RemoteControlProject) => void;
  onCreateProject: (name: string) => void;
  folderBacked: boolean;
  activeProject: RemoteControlProject | null;
  onRefresh?: () => void;
  onApply?: () => void;
  onDiscard?: () => void;
  bridgeOnline: boolean;
}

export function RemoteSidePanel({
  open,
  onOpenChange,
  session,
  target,
  space,
  projects,
  projectLoading,
  controlLoading,
  onSelectProject,
  onCreateProject,
  folderBacked,
  activeProject,
  onRefresh,
  onApply,
  onDiscard,
  bridgeOnline,
}: RemoteSidePanelProps) {
  const isDesktop = useIsDesktop();

  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [draftName, setDraftName] = useState('');

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onOpenChange(false);
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open, onOpenChange]);

  const openCreateDialog = () => {
    setDraftName('');
    setCreateDialogOpen(true);
  };

  const submitCreate = () => {
    const name = draftName.trim();
    if (!name || projectLoading) return;
    setCreateDialogOpen(false);
    setDraftName('');
    onCreateProject(name);
  };

  return (
    <>
      <ResponsivePanel
        open={open}
        onOpenChange={onOpenChange}
        side="left"
        motionKey="remote-side-panel"
        ariaLabelledBy="remote-side-panel-title"
      >
        <h2 id="remote-side-panel-title" className="sr-only">
          Remote control panel
        </h2>

        <div className="flex h-full min-h-0 flex-col overflow-hidden">
          {/* Header */}
          <header className="flex h-12 shrink-0 items-center gap-1 px-2 text-body-sm font-semibold text-ds-text-neutral-default-default">
            <Button
              variant="ghost"
              size="sm"
              buttonContent="icon-only"
              buttonRadius="full"
              aria-label="Fold sidebar"
              onClick={() => onOpenChange(false)}
            >
              <MorphIcon icon={PanelLeftClose} className="h-4 w-4" />
            </Button>
            <span className="min-w-0 flex-1 truncate">
              {space?.name || session?.space_name || 'Space'}
            </span>
            <Button
              variant="primary"
              size="sm"
              buttonContent="icon-only"
              buttonRadius="full"
              aria-label="Create project"
              disabled={!bridgeOnline || projectLoading}
              onClick={openCreateDialog}
            >
              <MorphIcon icon={Plus} className="h-4 w-4" />
            </Button>
          </header>

          {/* Content — no outer scroll; only project list scrolls */}
          <div className="flex min-h-0 flex-1 flex-col">
            {/* Project list */}
            <div className="flex min-h-0 flex-1 flex-col pl-1 pt-2">
              {projects.length > 0 ? (
                <div className="scrollbar-always-visible flex min-h-0 flex-1 flex-col gap-1.5 overflow-y-auto">
                  {projects.map((project) => {
                    const selected = project.id === target?.project_id;
                    return (
                      <button
                        key={project.id}
                        type="button"
                        className={cn(
                          'flex w-full shrink-0 items-center gap-2 rounded-xl border border-solid px-3 py-2 text-left text-body-sm transition hover:bg-ds-bg-neutral-subtle-default [&_svg]:text-current',
                          selected
                            ? 'border-transparent bg-ds-bg-neutral-subtle-default text-ds-text-neutral-default-default'
                            : 'border-transparent bg-transparent text-ds-text-neutral-muted-default'
                        )}
                        disabled={!bridgeOnline || projectLoading}
                        onClick={() => {
                          onSelectProject(project);
                          if (!isDesktop) onOpenChange(false);
                        }}
                      >
                        <MorphIcon
                          icon={selected ? Check : MessageCircle}
                          className="h-4 w-4 shrink-0"
                        />
                        <span className="truncate">
                          {project.name || project.id}
                        </span>
                      </button>
                    );
                  })}
                </div>
              ) : (
                <div className="flex items-center gap-2 rounded-xl bg-ds-bg-neutral-subtle-default px-3 py-2 text-body-sm text-ds-text-neutral-muted-default">
                  <MorphIcon icon={FolderPlus} className="h-4 w-4 shrink-0" />
                  Create a project to get started.
                </div>
              )}
            </div>

            {/* Folder operations */}
            {folderBacked && activeProject && (
              <>
                <div className="mx-4 shrink-0 border-t border-ds-border-neutral-subtle-disabled" />
                <div className="shrink-0 px-4 pb-4 pt-4">
                  <p className="mb-3 text-label-xs font-semibold uppercase tracking-wide text-ds-text-neutral-muted-default">
                    Folder ops
                  </p>
                  <div className="grid grid-cols-2 gap-2">
                    <Button
                      variant="secondary"
                      size="sm"
                      buttonContent="text"
                      buttonRadius="full"
                      className="w-full min-w-0"
                      disabled={!!controlLoading}
                      onClick={onRefresh}
                    >
                      <MorphIcon icon={RefreshCw} className="h-4 w-4" />
                      Refresh
                    </Button>
                    <Button
                      variant="secondary"
                      size="sm"
                      buttonContent="text"
                      buttonRadius="full"
                      className="w-full min-w-0"
                      disabled={!!controlLoading}
                      onClick={onApply}
                    >
                      <MorphIcon icon={CheckCheck} className="h-4 w-4" />
                      Apply
                    </Button>
                    <Button
                      variant="outline"
                      tone="error"
                      size="sm"
                      buttonContent="text"
                      buttonRadius="full"
                      className="col-span-2 w-full min-w-0"
                      disabled={!!controlLoading}
                      onClick={onDiscard}
                    >
                      <MorphIcon icon={Trash2} className="h-4 w-4" />
                      Discard
                    </Button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </ResponsivePanel>

      {/* Create project dialog */}
      {createDialogOpen &&
        createPortal(
          <>
            <div
              className="fixed inset-0 z-[60] bg-dialog-overlay-scrim"
              aria-hidden
              onClick={() => setCreateDialogOpen(false)}
            />
            <div className="pointer-events-none fixed inset-0 z-[61] flex items-center justify-center p-4">
              <div
                className="pointer-events-auto w-full max-w-sm rounded-2xl border border-solid border-ds-border-neutral-subtle-default bg-ds-bg-neutral-default-default p-6 shadow-2xl"
                onClick={(e) => e.stopPropagation()}
              >
                <h3 className="mb-4 text-base font-semibold text-ds-text-neutral-default-default">
                  New Project
                </h3>
                <input
                  autoFocus
                  value={draftName}
                  onChange={(e) => setDraftName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') submitCreate();
                    if (e.key === 'Escape') {
                      e.stopPropagation();
                      setCreateDialogOpen(false);
                    }
                  }}
                  placeholder="Project name…"
                  className="mb-4 w-full rounded-full border border-solid border-ds-border-neutral-subtle-default bg-ds-bg-neutral-subtle-default px-3 py-2 text-body-sm outline-none transition focus:border-ds-border-neutral-default-default"
                />
                <div className="flex justify-end gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    buttonContent="text"
                    buttonRadius="full"
                    onClick={() => setCreateDialogOpen(false)}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="primary"
                    size="sm"
                    buttonContent="text"
                    buttonRadius="full"
                    disabled={!draftName.trim() || projectLoading}
                    onClick={submitCreate}
                  >
                    Create
                  </Button>
                </div>
              </div>
            </div>
          </>,
          document.body
        )}
    </>
  );
}
